import {
  HYDRATE_ACTIVE_CONTEXT,
  SWITCH_CONTEXT,
  SWITCH_CONTEXT_SUCCESS,
  SWITCH_CONTEXT_FAIL,
  CLEAR_ACTIVE_CONTEXT_ERROR,
  REFRESH_AGGREGATE_VIEW_ORGS,
  REFRESH_ACTIVE_CONTEXT_SUCCESS,
} from './actionTypes';

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem('authUser') || '{}');
  } catch {
    return {};
  }
};

/**
 * Snapshot the active-context slice from localStorage. Called on app boot
 * (the slice's initial state) and again on HYDRATE_ACTIVE_CONTEXT after a
 * switch persists a new authUser.
 *
 * <p>Backwards compat: tokens issued by older builds carry
 * {@code activeContextType} (SUB_ORG / GLOBAL) instead of the new
 * {@code activeOrganizationId} field. We map the legacy GLOBAL value to a
 * null org id, and the legacy SUB_ORG value to {@code activeContextId}. The
 * legacy single-org pointers ({@code organization.id}, {@code hospital.id})
 * are intentionally NOT consulted — the backend always returns
 * {@code activeOrganizationId} on the login response, so a missing value
 * means Global View, not "fall back to whatever org I belong to".
 */
const snapshotFromStorage = () => {
  const authUser = readAuthUser();

  // Logged out (no persisted session): report neutral defaults. Without this
  // guard the missing activeOrganizationId key reads as "Global View", so the
  // first post-login render (before any HYDRATE_ACTIVE_CONTEXT) bounced every
  // org-scoped route to the global dashboard via AuthProtected's active-org gate.
  if (!authUser?.id) {
    return {
      activeOrganizationId: null,
      activeOrganizationName: null,
      activeOrganizationCurrency: null,
      activeOrganizationDefaultWorkOrderView: null,
      isGlobalView: false,
      memberships: [],
      canEnterGlobalView: false,
    };
  }

  const memberships = Array.isArray(authUser?.memberships) ? authUser.memberships : [];

  let activeOrganizationId = null;
  if (Object.prototype.hasOwnProperty.call(authUser || {}, 'activeOrganizationId')) {
    activeOrganizationId = authUser.activeOrganizationId ?? null;
  } else if (authUser?.activeContextType === 'GLOBAL') {
    activeOrganizationId = null;
  } else if (authUser?.activeContextId != null) {
    activeOrganizationId = authUser.activeContextId;
  }

  let activeOrganizationName = authUser?.activeContextName || null;
  if (!activeOrganizationName && activeOrganizationId != null) {
    const match = memberships.find((m) => m.organizationId === activeOrganizationId);
    activeOrganizationName = match?.organizationName ?? null;
  }

  // Currency (ISO 4217) of the active org. Single currency per org by design — see the
  // currency-uniformity work. Null in Global View; the value is refreshed by OrganizationSettings
  // and by the active-context refresh saga once the backend payload carries currency.
  const activeOrganizationCurrency = authUser?.activeOrganizationCurrency ?? null;
  // Default Work Orders list view of the active org; null in Global View / older snapshots.
  const activeOrganizationDefaultWorkOrderView = authUser?.activeOrganizationDefaultWorkOrderView ?? null;

  return {
    activeOrganizationId,
    activeOrganizationName,
    activeOrganizationCurrency,
    activeOrganizationDefaultWorkOrderView,
    isGlobalView: activeOrganizationId == null,
    memberships,
    canEnterGlobalView: !!authUser?.canEnterGlobalView,
  };
};

const DEFAULTS = {
  activeOrganizationId: null,
  activeOrganizationName: null,
  activeOrganizationCurrency: null,
  activeOrganizationDefaultWorkOrderView: null,
  isGlobalView: false,
  memberships: [],
  canEnterGlobalView: false,
  switching: false,
  error: null,
};

// Bootstrap the slice from localStorage on module load so the very first
// render of any page already sees the persisted active context — no race with
// ProfileMenu's hydration effect.
const INIT_STATE = { ...DEFAULTS, ...snapshotFromStorage() };

const ActiveContext = (state = INIT_STATE, action) => {
  switch (action.type) {
    case HYDRATE_ACTIVE_CONTEXT:
      return { ...state, ...snapshotFromStorage(), switching: false, error: null };

    case SWITCH_CONTEXT:
      return { ...state, switching: true, error: null };

    case SWITCH_CONTEXT_SUCCESS: {
      const payload = action.payload || {};
      return {
        ...state,
        switching: false,
        error: null,
        activeOrganizationId: payload.activeOrganizationId ?? null,
        activeOrganizationName: payload.activeContextName ?? null,
        activeOrganizationCurrency: payload.activeOrganizationCurrency ?? null,
        activeOrganizationDefaultWorkOrderView: payload.activeOrganizationDefaultWorkOrderView ?? null,
        isGlobalView: payload.activeOrganizationId == null,
        canEnterGlobalView: typeof payload.canEnterGlobalView === 'boolean'
          ? payload.canEnterGlobalView
          : state.canEnterGlobalView,
      };
    }

    case SWITCH_CONTEXT_FAIL:
      return { ...state, switching: false, error: action.payload };

    case CLEAR_ACTIVE_CONTEXT_ERROR:
      return { ...state, error: null };

    case REFRESH_ACTIVE_CONTEXT_SUCCESS: {
      const payload = action.payload || {};
      const memberships = Array.isArray(payload.memberships) ? payload.memberships : [];
      const nextActiveOrgId = payload.activeOrganizationId ?? null;

      // Mirror the refreshed envelope into authUser so a page reload picks up
      // the same fresh state and doesn't snap back to the login-time snapshot.
      try {
        const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
        authUser.memberships = memberships;
        authUser.activeOrganizationId = nextActiveOrgId;
        authUser.activeContextName = payload.activeContextName ?? null;
        authUser.activeOrganizationCurrency = payload.activeOrganizationCurrency ?? null;
        authUser.activeOrganizationDefaultWorkOrderView = payload.activeOrganizationDefaultWorkOrderView ?? null;
        authUser.isGlobalView = !!payload.globalView || nextActiveOrgId == null;
        authUser.canEnterGlobalView = !!payload.canEnterGlobalView;
        localStorage.setItem('authUser', JSON.stringify(authUser));
      } catch {
        // Storage failures are non-fatal — Redux is already updated.
      }

      return {
        ...state,
        memberships,
        activeOrganizationId: nextActiveOrgId,
        activeOrganizationName: payload.activeContextName ?? null,
        activeOrganizationCurrency: payload.activeOrganizationCurrency ?? null,
        activeOrganizationDefaultWorkOrderView: payload.activeOrganizationDefaultWorkOrderView ?? null,
        isGlobalView: !!payload.globalView || nextActiveOrgId == null,
        canEnterGlobalView: !!payload.canEnterGlobalView,
      };
    }

    case REFRESH_AGGREGATE_VIEW_ORGS: {
      const liveIds = new Set(action.payload?.aggregateOrgIds || []);
      const nextMemberships = (state.memberships || []).map((m) => ({
        ...m,
        hasAggregateView: liveIds.has(m.organizationId),
      }));

      // Persist the reconciled flags so a page refresh doesn't snap back to
      // the stale snapshot from login.
      try {
        const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
        if (Array.isArray(authUser.memberships)) {
          authUser.memberships = authUser.memberships.map((m) => ({
            ...m,
            hasAggregateView: liveIds.has(m.organizationId),
          }));
          localStorage.setItem('authUser', JSON.stringify(authUser));
        }
      } catch {
        // Storage failures are non-fatal — the in-memory slice is already updated.
      }

      return { ...state, memberships: nextMemberships };
    }

    default:
      return state;
  }
};

export default ActiveContext;
